import { useState } from 'react';

import { Control, FieldValues, Path, PathValue, useWatch } from 'react-hook-form';
import { useUpdateEffect } from 'react-use';
import { Option } from '../../general-types';

type UseFormDependentOptionsProps<
  T extends FieldValues,
  TParentName extends Path<T>,
  TChildName extends Path<T>,
> = {
  control: Control<T>;
  parentName: TParentName;
  childName: TChildName;
  getOptions: (parentValue: PathValue<T, TParentName>) => Option[];
  onResetField: (name: TChildName) => void;
};

/**
 * Hook for managing dependent select/autocomplete options in React Hook Form.
 * Only for syncronous dependencies.
 *
 * Behavior:
 * - Returns empty options array when parent field is empty
 * - Updates options when parent field value changes
 * - Automatically resets child field if current value is not available in new options
 * - Preserves child field value if it remains valid after parent change
 * - First reset after update options to omit invalid option/value dependency
 */
export const useFormDependentOptions = <
  T extends FieldValues,
  TParentName extends Path<T>,
  TChildName extends Path<T>,
>({
  control,
  parentName,
  childName,
  getOptions,
  onResetField,
}: UseFormDependentOptionsProps<T, TParentName, TChildName>) => {
  const parentValue = useWatch({
    name: parentName,
    control,
  });

  const childValue = useWatch({
    name: childName,
    control,
  });

  const [availableOptions, setAvailableOptions] = useState<Option[]>(
    getOptions(parentValue),
  );

  const updateOptions = () => {
    const newOptions = getOptions(parentValue);

    const isCurrentOptionAvailable = newOptions.some(
      (option) => option.value === childValue,
    );

    if (!isCurrentOptionAvailable) {
      onResetField(childName);
    }

    setAvailableOptions(newOptions);
  };

  useUpdateEffect(() => {
    updateOptions();
  }, [parentValue]);

  return {
    availableOptions,
  };
};
